import React, { useMemo } from 'react';
import { message } from 'antd';
import { FileOutlined } from '@ant-design/icons';
import ProjectSubmissionModal from '@/components/module/projects/ProjectSubmissionModal';
import MilestoneLinkModal from '@/components/module/projects/milestone_tab/MilestoneLinkModal';
import { PendingAttachmentTile, PendingLinkWrap } from '@/components/module/projects/sprints_tab/PendingRichTextAttachments';
import { PanelSectionLabel } from '@/pages/inside/tenant/style';
import {
    getSprintTaskApiId,
    getSprintTaskNumberLabel,
    getTaskDescriptionMarkdown,
    truncateFileName,
} from '@hooks/projects/projectHelpers';

/**
 * Submit sprint tasks to QA via POST /projects/:projectId/qa/submit.
 * Opens when user queues task(s) for submission from the Sprints Kanban.
 * Routing is determined by project module flags (isQA, isPMO):
 *   - Both on  → show QA/PMO toggle (QA default)
 *   - QA only  → read-only "Submit to QA" chip
 *   - PMO only → read-only "Submit to PMO" chip
 *   - Neither  → memo path, no destination chip
 */
export default function SprintSubmitToQAModal({
    open,
    onCancel,
    submissionGroupedBySprintEnriched,
    submissionNotes,
    setSubmissionNotes,
    submitToQA,
    submitToQAPending,
    removeFromSubmission,
    submissionSession,
    onOpenTaskDetail,
    mentionUsers = [],
    mentionMilestones = [],
    mentionSprints = [],
    submissionPendingFiles = [],
    submissionPendingLinks = [],
    handleSubmissionFilesSelected,
    handleSubmissionLinkRequest,
    handleSubmissionLinkModalSubmit,
    submissionLinkModalOpen,
    closeSubmissionLinkModal,
    submissionLinkForm,
    removeSubmissionPendingFile,
    removeSubmissionPendingLink,
    handleSubmissionEditorLinkRemoved,
    isAttachment = true,
    isLink = true,
    isMention = true,
    /** Submission routing flags */
    isQAEnabled = true,
    isPMOEnabled = false,
    submittedTo = 'qa',
    setSubmittedTo,
}) {
    /** Resolve destination label for display */
    const destinationLabel = submittedTo === 'pmo' ? 'PMO' : 'QA';

    /** Both QA and PMO enabled → show selector. One enabled → read-only. Neither → memo, no label. */
    const bothEnabled = isQAEnabled && isPMOEnabled;
    const onlyQA = isQAEnabled && !isPMOEnabled;
    const isMemo = !isQAEnabled && !isPMOEnabled;

    const handleSubmit = () => {
        submitToQA({
            onSuccess: () => message.success(`Submitted to ${destinationLabel}`),
            onError: (err) =>
                message.error(err?.response?.data?.message || err?.message || `Failed to submit to ${destinationLabel}`),
        });
    };

    const taskRows = useMemo(() => {
        const groups = Array.isArray(submissionGroupedBySprintEnriched) ? submissionGroupedBySprintEnriched : [];
        const rows = [];
        for (const g of groups) {
            const sprint = g.fullSprint;
            const tasks = Array.isArray(g.fullTasks) ? g.fullTasks : [];
            for (const task of tasks) {
                const id = getSprintTaskApiId(task) ?? task?.id;
                if (id == null) continue;
                const descriptionMarkdown = getTaskDescriptionMarkdown(task);
                rows.push({
                    key: `${g.sprintId}-${id}`,
                    taskIdLabel: getSprintTaskNumberLabel(task),
                    title: task?.title ?? task?.name ?? '—',
                    ...(descriptionMarkdown ? { descriptionMarkdown } : {}),
                    removeType: 'task',
                    removeKey: id,
                    payload: { sprint, task },
                });
            }
        }
        return rows;
    }, [submissionGroupedBySprintEnriched]);

    const handleTaskClick = (payload) => {
        if (!payload?.task || !onOpenTaskDetail) return;
        onOpenTaskDetail(payload.sprint, payload.task);
    };

    const sidLabel = `SID-${submissionSession > 0 ? submissionSession : 1}`;
    const submissionTitle = `Submission ${submissionSession > 0 ? submissionSession : 1}`;

    const richTextEditorProps = useMemo(
        () => ({
            showAttachButton: isAttachment,
            onFilesSelected: isAttachment ? handleSubmissionFilesSelected : undefined,
            onLinkRequest: isLink ? handleSubmissionLinkRequest : undefined,
            onLinkRemoved: isLink && typeof handleSubmissionEditorLinkRemoved === 'function' ? handleSubmissionEditorLinkRemoved : undefined,
            onAttachmentRemoved:
                isAttachment && typeof removeSubmissionPendingFile === 'function'
                    ? (index) => {
                          const p = submissionPendingFiles[index];
                          if (p) removeSubmissionPendingFile(p.id);
                      }
                    : undefined,
            attachmentIndexOffset: submissionPendingFiles.length,
        }),
        [
            isAttachment,
            isLink,
            handleSubmissionFilesSelected,
            handleSubmissionLinkRequest,
            handleSubmissionEditorLinkRemoved,
            removeSubmissionPendingFile,
            submissionPendingFiles,
        ],
    );

    const attachmentsBlock = useMemo(() => {
        const files = Array.isArray(submissionPendingFiles) ? submissionPendingFiles : [];
        const links = Array.isArray(submissionPendingLinks) ? submissionPendingLinks : [];
        if (files.length === 0 && links.length === 0) return null;

        return (
            <div
                style={{
                    marginTop: 8,
                    marginBottom: 16,
                    padding: '8px 0',
                    borderTop: '1px solid var(--ant-color-border, #f0f0f0)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0,
                }}
            >
                {files.length > 0 ? (
                    <>
                        <PanelSectionLabel
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                marginBottom: 8,
                                fontSize: 11,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                                opacity: 0.75,
                            }}
                        >
                            <FileOutlined />
                            Attachments
                        </PanelSectionLabel>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                gap: 10,
                            }}
                        >
                            {files.map((p) => (
                                <PendingAttachmentTile
                                    key={p.id}
                                    item={p}
                                    onRemove={() => removeSubmissionPendingFile?.(p.id)}
                                    truncateFileName={truncateFileName}
                                    showBottomFileName={false}
                                />
                            ))}
                        </div>
                    </>
                ) : null}
                {links.length > 0 ? (
                    <div style={{ marginTop: files.length > 0 ? 12 : 0 }}>
                        <PendingLinkWrap links={links} onRemoveLink={removeSubmissionPendingLink} />
                    </div>
                ) : null}
            </div>
        );
    }, [submissionPendingFiles, submissionPendingLinks, removeSubmissionPendingFile, removeSubmissionPendingLink]);

    /**
     * Destination selector block rendered above the description:
     * - Both QA + PMO: pill toggle, QA default.
     * - Only QA: read-only chip "Submit to QA".
     * - Only PMO: read-only chip "Submit to PMO".
     * - Neither (memo): nothing shown.
     */
    const destinationBlock = useMemo(() => {
        if (isMemo) return null;

        const chipStyle = {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 12px',
            borderRadius: 20,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.03em',
            background: 'var(--ant-color-primary-bg, rgba(124,58,237,0.08))',
            color: 'var(--ant-color-primary, #7c3aed)',
            border: '1px solid var(--ant-color-primary-border, rgba(124,58,237,0.25))',
            userSelect: 'none',
        };

        const wrapStyle = {
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 16,
            paddingBottom: 14,
            borderBottom: '1px solid var(--ant-color-border, #f0f0f0)',
        };

        const labelStyle = {
            fontSize: 12,
            fontWeight: 500,
            color: 'var(--ant-color-text-secondary, #6b7280)',
            flexShrink: 0,
        };

        if (bothEnabled) {
            return (
                <div style={wrapStyle}>
                    <span style={labelStyle}>Submit to</span>
                    <div
                        style={{
                            display: 'inline-flex',
                            borderRadius: 20,
                            border: '1px solid var(--ant-color-border, #d9d9d9)',
                            overflow: 'hidden',
                        }}
                    >
                        {['qa', 'pmo'].map((opt) => {
                            const active = submittedTo === opt;
                            return (
                                <button
                                    key={opt}
                                    type="button"
                                    onClick={() => setSubmittedTo?.(opt)}
                                    style={{
                                        padding: '4px 18px',
                                        fontSize: 12,
                                        fontWeight: 600,
                                        border: 'none',
                                        outline: 'none',
                                        cursor: 'pointer',
                                        transition: 'background 0.18s, color 0.18s',
                                        background: active
                                            ? 'var(--ant-color-primary, #7c3aed)'
                                            : 'transparent',
                                        color: active
                                            ? '#fff'
                                            : 'var(--ant-color-text-secondary, #6b7280)',
                                        letterSpacing: '0.04em',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {opt === 'qa' ? 'QA' : 'PMO'}
                                </button>
                            );
                        })}
                    </div>
                </div>
            );
        }

        /* Read-only chip for single-route projects */
        const chipLabel = onlyQA ? 'Submit to QA' : 'Submit to PMO';
        return (
            <div style={wrapStyle}>
                <span style={labelStyle}>Destination</span>
                <span style={chipStyle}>{chipLabel}</span>
            </div>
        );
    }, [isMemo, bothEnabled, onlyQA, submittedTo, setSubmittedTo]);

    return (
        <>
            <MilestoneLinkModal
                open={submissionLinkModalOpen}
                onCancel={closeSubmissionLinkModal}
                linkModalForm={submissionLinkForm}
                handleLinkModalSubmit={handleSubmissionLinkModalSubmit}
            />
            <ProjectSubmissionModal
                open={open}
                onCancel={onCancel}
                modalSize="md"
                variant="qa"
                sidLabel={sidLabel}
                submissionTitle={submissionTitle}
                descriptionHtml={submissionNotes}
                onDescriptionChange={setSubmissionNotes}
                isAttachment={isAttachment}
                isLink={isLink}
                isMention={isMention}
                mentionUsers={mentionUsers}
                mentionMilestones={mentionMilestones}
                mentionSprints={mentionSprints}
                richTextEditorProps={richTextEditorProps}
                beforeDescription={destinationBlock}
                afterDescription={attachmentsBlock}
                taskRows={taskRows}
                onTaskRowClick={handleTaskClick}
                onRemoveTask={removeFromSubmission}
                onSubmit={handleSubmit}
                submitPending={submitToQAPending}
                emptyTasksMessage="Nothing selected yet."
                submitLabel={isMemo ? 'Submit' : `Submit to ${destinationLabel}`}
            />
        </>
    );
}
